import { useState, type FormEvent, type ReactNode } from 'react';
import { LogOut, ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth, type Gate } from '@/lib/auth';
import { Mark, Wordmark } from '@/components/Logo';

/**
 * The way in. Two screens, one frame: an ink panel carrying the mark (on a
 * phone it shrinks to a band at the top), and a paper column with the one
 * thing to do next. Seen rarely, so it says plainly what is happening and why.
 */
export function GateScreen({ gate }: { gate: Exclude<Gate, { step: 'ready' } | { step: 'loading' }> }) {
  switch (gate.step) {
    case 'signed-out':
      return <SignIn />;
    case 'not-staff':
      return <NotStaff email={gate.session.user.email ?? 'this account'} />;
  }
}

export function Booting() {
  return (
    <div className="grid min-h-dvh place-items-center" aria-busy="true" aria-label="Opening Desk">
      <Mark className="size-8 text-ink/25" />
    </div>
  );
}

function Frame({ title, lede, children }: { title: string; lede: ReactNode; children: ReactNode }) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(20rem,38%)_1fr]">
      <aside
        data-field="ink"
        className="flex items-center gap-3 bg-bg px-5 py-4 text-fg lg:flex-col lg:items-start lg:justify-between lg:p-10"
      >
        <div className="flex items-center gap-3">
          <Mark className="size-7 lg:size-9" />
          <Wordmark className="h-3.5 lg:h-4" />
        </div>
        <p className="ml-auto font-display text-sm font-semibold tracking-tight text-fg-dim lg:ml-0 lg:text-2xl lg:text-fg">
          Desk
        </p>
      </aside>
      <main className="flex items-start justify-center px-5 py-10 sm:items-center lg:px-10">
        <div className="w-full max-w-sm">
          <h1 className="page-title">{title}</h1>
          <div className="mt-2 text-fg-dim">{lede}</div>
          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}

function message(err: unknown) {
  const text = err instanceof Error ? err.message : String(err);
  if (/invalid login credentials/i.test(text)) return 'That email and password do not match an account on Desk.';
  if (/fetch|network/i.test(text)) return 'Could not reach the server. Check the connection and try again.';
  return text;
}

/* ------------------------------------------------------------------ sign in */

function SignIn() {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: String(form.get('email')).trim(),
      password: String(form.get('password')),
    });
    if (error) {
      setError(message(error));
      setBusy(false);
      return;
    }
    await refresh();
  }

  return (
    <Frame title="Sign in" lede="The studio's side of bitbew.com.">
      <form onSubmit={onSubmit} className="grid gap-5" noValidate>
        <div className="field">
          <label className="label" htmlFor="email">
            Email
          </label>
          <input id="email" name="email" type="email" autoComplete="username" required className="input" />
        </div>
        <div className="field">
          <label className="label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            className="input"
          />
        </div>
        {error && (
          <p role="alert" className="error-text">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-lg w-full" disabled={busy}>
          {busy ? 'Checking…' : 'Continue'}
        </button>
        <p className="hint">
          There is no sign-up. Accounts are created in the Supabase dashboard, and a forgotten password is reset
          there too.
        </p>
      </form>
    </Frame>
  );
}

/* --------------------------------------------------------------- refused */

function NotStaff({ email }: { email: string }) {
  const { signOut } = useAuth();
  return (
    <Frame
      title="Not on Desk"
      lede={
        <>
          <span className="font-semibold text-fg">{email}</span> signed in correctly, but it isn't one of the
          studio's Desk accounts, so there is nothing to show it.
        </>
      }
    >
      <div className="flex items-start gap-3 rounded-lg border border-edge bg-bg-raised p-4">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-fg-dim" aria-hidden />
        <p className="text-sm text-fg-dim">
          Desk access is granted by adding the account to the <code className="num">staff</code> table in a
          migration. If this should be a Desk account, that is the step that is missing.
        </p>
      </div>
      <button type="button" onClick={signOut} className="btn btn-quiet mt-6">
        <LogOut className="size-4" aria-hidden /> Sign out
      </button>
    </Frame>
  );
}
